import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { Hospital } from '../../../../shared/api/hscan/hospital';
import { BlueRoundedButtonWithIcon } from '../../common/components/BlueRoundedButton';
import {
  CheckboxText,
  CheckboxWithText,
} from '../../common/components/CheckboxWithText';
import {
  HospitalAutocompleteSlot,
  RrnSlot,
} from '../../common/components/Slot';
import {
  SlotGrid,
  SlotGridWrapper,
} from '../../common/components/Slot/SlotWrapper';
import {
  SectionContentContainer,
  SectionHeading,
} from '../../common/components/styledComponents';

const RequestImagesSlotGrid = styled(SlotGrid)`
  row-gap: 20px;
`;

const RrnNoticeMessage = styled.div`
  ${p => p.theme.isMobile && 'width: 262px;'};
  margin-top: ${p => (p.theme.isMobile ? '30px' : '27px')};
  text-align: center;
  font-size: 14px;
  line-height: 20px;
  color: #7a7a7e;
  word-break: keep-all;
`;

const OnlineIssuanceMessageWrapper = styled.div`
  margin-top: 5px;
  font-size: 12px;
  color: #7a7a7a;
`;

interface SearchHospitalSectionProps {
  hospital: Hospital | null;
  rrNumber: string;
  onChangeHospital: (hospital: Hospital | null) => void;
  onChangeRrNumber: (rrn: string) => void;
  onSearchImage: () => void;
  onChangeQuery: () => void;
}
export const SearchHospitalSection = (props: SearchHospitalSectionProps) => {
  const {
    hospital,
    rrNumber,
    onChangeHospital,
    onChangeRrNumber,
    onSearchImage,
    onChangeQuery,
  } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.request.search',
  });

  /* 1. 병원을 골랐고, 2. 주민번호 요구하면 주민번호가 있어야 함 */
  const isSearchAvailable = useMemo(
    () =>
      hospital &&
      (!hospital.registrationNumberRequired || /\d{6}-\d{7}/.test(rrNumber)),
    [hospital, rrNumber],
  );

  const [consented, setConsented] = useState(false);
  useEffect(() => {
    setConsented(false);
  }, [hospital?.id]);

  return (
    <SectionContentContainer>
      <SectionHeading>{t('heading')}</SectionHeading>
      <SlotGridWrapper>
        <RequestImagesSlotGrid $marginTop="37px" $width={394} $columnGap={26}>
          <HospitalAutocompleteSlot
            label={`${t('inputs.hospital.title')}:`}
            value={hospital}
            onChangeQuery={onChangeQuery}
            onChangeHospital={onChangeHospital}
            loadEnabledOnly
          />
          {hospital?.registrationNumberRequired && (
            <RrnSlot
              label={`${t('inputs.rrn.title')}`}
              value={rrNumber}
              onChange={onChangeRrNumber}
            />
          )}
        </RequestImagesSlotGrid>
      </SlotGridWrapper>
      {hospital?.registrationNumberRequired && (
        <RrnNoticeMessage>
          <Trans t={t} i18nKey="inputs.rrn.notice" components={[<br />]} />
        </RrnNoticeMessage>
      )}
      {hospital?.onlineIssuanceMessage && (
        <OnlineIssuanceMessageWrapper>
          {hospital.onlineIssuanceMessage}
        </OnlineIssuanceMessageWrapper>
      )}
      <CheckboxWithText
        text={<CheckboxText>{t('consent')}</CheckboxText>}
        $margin="18px 0 0 0"
        value={consented}
        onChange={setConsented}
      />
      <BlueRoundedButtonWithIcon
        maxWidth="213px"
        marginTop="18px"
        onClick={onSearchImage}
        disabled={!isSearchAvailable || !consented}>
        {t('button')}
      </BlueRoundedButtonWithIcon>
    </SectionContentContainer>
  );
};
